import axios, { AxiosInstance, AxiosRequestConfig, CancelToken } from "axios";
import { getAsyncTokens, setAsyncClearAll, setAsyncTokens } from "./AsyncStorage";

const baseURL = process.env.REACT_APP_BASE_URI || "http://api.only4values.com/v1/api-service";

console.log(baseURL);

export const endpoints = {
  baseUri: baseURL,
  refreshToken: "/auth/refresh",
};

export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

export const axiosInstance: AxiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.response.use(
  (response) => response, // For success return the response as is
  async (error) => {
    // Log the error

    // Store the original request
    const originalReq = error.config;

    // Check if the response is having error code as 401
    // and is not a retry (to avoid infinite retries) and
    // avoid checking on the authenticate URL as it may be due to user
    // entering wrong password.
    // eslint-disable-next-line eqeqeq
    if (error.code === "ERR_NETWORK") {
      const error = {
        response: {
          data: {
            message: "Network Error",
            error: "Server is not reachable",
          },
          status: 502,
        },
      };
      return Promise.reject(error);
    }

    const shouldRefresh = error.response?.status === 401 && !originalReq._retry;

    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    // Set the retry flag to true
    originalReq._retry = true;

    const tokens = await getAsyncTokens();

    if (!tokens.refresh.token) {
      return Promise.reject(error);
    }

    try {
      const response = await axiosInstance.post(`${endpoints.baseUri}${endpoints.refreshToken}`, {
        token: tokens.refresh.token,
      });
      await setAsyncTokens(response.data.tokens);
      originalReq.headers.Authorization = `Bearer ${response.data.tokens.access.token}`;
      return axiosInstance(originalReq);
    } catch (error) {
      console.log(`Error while refreshing token: ${error}, so logging out the user`);
      await setAsyncClearAll();
      return Promise.reject(error);
    }
  }
);

const getQuery = (formData: Record<string, unknown>): string => {
  if (!formData || Object.keys(formData).length === 0) return "";
  const searchParams = new URLSearchParams();
  const flattenObject = (obj: Record<string, unknown>, parentKey = ""): void => {
    Object.entries(obj).forEach(([key, value]) => {
      const newKey = parentKey ? `${parentKey}[${key}]` : key;
      if (typeof value === "object" && value !== null) {
        flattenObject(value as Record<string, unknown>, newKey);
      } else {
        searchParams.append(newKey, String(value));
      }
    });
  };
  flattenObject(formData);
  return `?${searchParams.toString()}`;
};

export enum HttpMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  DELETE = "delete",
}

export interface IDataFetcherProps<T = Record<string, unknown>> {
  url: string;
  method: HttpMethod;
  payload?: T;
  cancelToken?: CancelToken;
}


const dataFetcher = async <T = unknown>(props: IDataFetcherProps<T>): Promise<any> => {
  const { method, payload, cancelToken } = props;
  let { url } = props;
  
  if (method === HttpMethod.GET && payload) {
    url = `${url}${getQuery(payload)}`;
  }

  const tokens = await getAsyncTokens();
  const headers = {
    Authorization: `Bearer ${tokens?.access?.token}`,
  };

  const config: AxiosRequestConfig<T> = {
    method,
    url,
    data: payload,
    headers,
    cancelToken,
  };

  try {
    const response = await axiosInstance(config);
    return {
      error: false,
      data: response.data,
    };
  } catch (error) {
    console.error(`Error fetching data: ${error}`);
    return {
      error: true,
      message: error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};

export default dataFetcher;